'use client';

import { useCallback, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { DOCUMENT_PDF_URL_QUERY } from '@paeffgen/react/crm-documents/data-access-graphql';
import {
  useCanShareFiles,
  sanitizePdfFilename,
  sharePdfFromUrl,
} from '@paeffgen/react/crm-documents/util';

/**
 * Bundles the "share PDF" interaction for document rows and the detail view:
 * resolves the presigned URL via {@link DOCUMENT_PDF_URL_QUERY}, then shares the
 * PDF as a file (Web Share API) and falls back to the normal download
 * (`window.open`) when sharing is unsupported or fails.
 *
 * Uses its own lazy query so the existing download handlers stay untouched.
 *
 * @param adminQueryVars Extra GraphQL variables from `useAdmin()` (allCustomers / customerNumbers).
 */
export function usePdfShare(adminQueryVars: Record<string, unknown>) {
  const canShare = useCanShareFiles();
  const [isSharing, setIsSharing] = useState(false);
  const [fetchPdfUrl] = useLazyQuery<{ documentPdfUrl: string }>(
    DOCUMENT_PDF_URL_QUERY,
    { fetchPolicy: 'network-only' }
  );

  const sharePdf = useCallback(
    async (documentId: string, documentNumber: string) => {
      setIsSharing(true);
      try {
        const result = await fetchPdfUrl({
          variables: { documentId, ...adminQueryVars },
        });
        const url = result.data?.documentPdfUrl;
        if (!url) return;

        const outcome = await sharePdfFromUrl(
          url,
          sanitizePdfFilename(documentNumber)
        );
        if (outcome === 'fallback') {
          window.open(url, '_blank');
        }
      } finally {
        setIsSharing(false);
      }
    },
    [fetchPdfUrl, adminQueryVars]
  );

  return { canShare, isSharing, sharePdf };
}
